import * as React from "react"

import { cn } from "@/lib/utils"

// The page is the scroller: no overflow box here, or the sticky <thead> would
// stick to this wrapper instead of to the page and a second vertical scrollbar
// would appear. Pass wrapperClassName="overflow-x-auto" only where a table is
// genuinely too wide, accepting that its head stops sticking.
const Table = React.forwardRef(({ className, wrapperClassName, ...props }, ref) => (
  <div className={cn("relative w-full", wrapperClassName)}>
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-body", className)}
      {...props} />
  </div>
))
Table.displayName = "Table"

// The sticky head is parked under the console via `--console-h`. That offset is
// measured from the nearest SCROLLPORT, so if a caller wraps the table in an
// overflow box the head is pushed that many pixels down INSIDE the table — it
// lands between row 1 and row 2 rather than at the top (Instellingen, 02-08-2026).
// A head in the wrong place is worse than a head that does not stick, so this
// detects the wrapper and simply stops sticking.
const TableHeader = React.forwardRef(({ className, ...props }, ref) => {
  const own = React.useRef(null);
  const [sticky, setSticky] = React.useState(true);

  React.useEffect(() => {
    const node = own.current;
    if (!node || typeof getComputedStyle === "undefined") return;
    let n = node.parentElement;
    while (n && n !== document.body) {
      if (n.tagName === "MAIN") break; // the page scroller: this is the intended one
      const cs = getComputedStyle(n);
      if ([cs.overflow, cs.overflowX, cs.overflowY].some((v) => v && v !== "visible")) {
        setSticky(false);
        return;
      }
      n = n.parentElement;
    }
    setSticky(true);
  }, []);

  return (
    <thead
      ref={(node) => { own.current = node; if (typeof ref === "function") ref(node); else if (ref) ref.current = node; }}
      data-sticky={sticky ? "true" : "false"}
      style={sticky ? { top: "var(--console-h)" } : undefined}
      className={cn(
        `${sticky ? "sticky" : ""} z-10 bg-card [&_tr]:border-b [&_tr]:border-border`,
        className
      )}
      {...props} />
  );
})
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props} />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "transition-colors even:bg-zebra hover:bg-accent data-[state=selected]:bg-accent",
      className
    )}
    {...props} />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      // The background belongs on the cell, not on <thead>: a sticky thead
      // background is not painted over scrolling rows in Chrome.
      "h-9 bg-card px-3 text-left align-middle text-label text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "h-14 px-3 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-body text-muted-foreground", className)}
    {...props} />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
